import { RotateCw, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  useAdminJobs,
  useClearJobSet,
  useDeleteJob,
  useRetryJob,
} from "../../api/admin";
import { AdminJobSet } from "../../types/admin";
import { formatDateTime } from "../../utils/dateUtils";
import { DEFAULT_PAGE } from "./pagination";
import { notify } from "./notify";
import {
  Button,
  Card,
  Empty,
  Loading,
  PageHeader,
  StatCard,
  PaginationBar,
} from "./ui";

const SET_LABELS: Record<AdminJobSet, string> = {
  retry: "En échec (retry)",
  dead: "Morts",
  scheduled: "Programmés",
};

const fromEpoch = (value?: number | string | null) =>
  value == null
    ? null
    : typeof value === "number"
      ? new Date(value * 1000).toISOString()
      : value;

export const AdminJobs = () => {
  const [set, setSetState] = useState<AdminJobSet>("retry");
  const [page, setPage] = useState(DEFAULT_PAGE);
  const { data, isLoading } = useAdminJobs(set, page);

  const setSet = (value: AdminJobSet) => {
    setSetState(value);
    setPage((p) => ({ ...p, page: 1 }));
  };
  const retry = useRetryJob();
  const remove = useDeleteJob();
  const clear = useClearJobSet();

  if (isLoading || !data) return <Loading />;

  const jobs = data.jobs;

  return (
    <>
      <PageHeader title="Jobs Sidekiq" subtitle="Rafraîchi toutes les 10 s" />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard label="En file" value={data.stats.enqueued} />
        <StatCard label="En cours" value={data.busy.length} />
        <StatCard label="Retries" value={data.stats.retries} />
        <StatCard label="Morts" value={data.stats.dead} />
        <StatCard label="Programmés" value={data.stats.scheduled} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card title="Files d'attente">
          <dl className="divide-y divide-gray-100 text-sm">
            {data.queues.length === 0 ? (
              <Empty>Toutes les files sont vides.</Empty>
            ) : (
              data.queues.map((queue) => (
                <div
                  key={queue.name}
                  className="flex justify-between px-5 py-2.5"
                >
                  <dt className="text-gray-600">{queue.name}</dt>
                  <dd className="font-semibold text-gray-900">
                    {queue.size}
                    {queue.latency > 0 && (
                      <span className="ml-2 text-xs font-normal text-gray-400">
                        attente {queue.latency}s
                      </span>
                    )}
                  </dd>
                </div>
              ))
            )}
          </dl>
        </Card>

        <Card
          title={`En cours (${data.busy.length}) · ${data.processes.length} process`}
        >
          {data.busy.length === 0 ? (
            <Empty>Aucun job en cours.</Empty>
          ) : (
            <ul className="divide-y divide-gray-100 text-sm">
              {data.busy.map((job, i) => (
                <li key={i} className="px-5 py-2.5">
                  <p className="font-medium text-gray-900">{job.klass}</p>
                  <p className="text-xs text-gray-500">
                    {job.queue} · args {JSON.stringify(job.args)} · depuis{" "}
                    {formatDateTime(fromEpoch(job.run_at))}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1">
          {(Object.keys(SET_LABELS) as AdminJobSet[]).map((value) => (
            <button
              key={value}
              onClick={() => setSet(value)}
              className={`px-4 py-1.5 text-sm font-medium rounded-md ${
                set === value
                  ? "bg-indigo-600 text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {SET_LABELS[value]} ({data.set_sizes[value]})
            </button>
          ))}
        </div>
        {jobs.length > 0 && (
          <Button
            variant="danger"
            onClick={() => clear.mutate(set, notify("Liste vidée"))}
            disabled={clear.isLoading}
            confirm={`Vider toute la liste « ${SET_LABELS[set]} » ?`}
          >
            <Trash2 className="w-4 h-4" /> Tout vider
          </Button>
        )}
      </div>

      <Card>
        {jobs.length === 0 ? (
          <Empty>Rien ici.</Empty>
        ) : (
          <ul className="divide-y divide-gray-100">
            {jobs.map((job) => (
              <li
                key={job.jid}
                className="px-5 py-3 flex items-start justify-between gap-4"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {job.klass}{" "}
                    <span className="text-xs font-normal text-gray-500">
                      {job.queue} · args {JSON.stringify(job.args)}
                    </span>
                  </p>
                  {job.error_class && (
                    <p className="text-xs text-red-600 break-words">
                      {job.error_class} : {job.error_message}
                    </p>
                  )}
                  <p className="text-xs text-gray-400">
                    {set === "scheduled" ? "Prévu le" : "Dernier essai"}{" "}
                    {formatDateTime(fromEpoch(job.at))}
                    {job.retry_count != null &&
                      ` · ${job.retry_count + 1} tentative(s)`}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    onClick={() =>
                      retry.mutate({ set, jid: job.jid }, notify("Job relancé"))
                    }
                    disabled={retry.isLoading}
                  >
                    <RotateCw className="w-3.5 h-3.5" />{" "}
                    {set === "scheduled" ? "Lancer maintenant" : "Relancer"}
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() =>
                      remove.mutate(
                        { set, jid: job.jid },
                        notify("Job supprimé"),
                      )
                    }
                    disabled={remove.isLoading}
                    confirm="Supprimer ce job ?"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <PaginationBar pagination={data.pagination} onChange={setPage} />
      </Card>
    </>
  );
};
